import { useEffect, useState } from "react";
import { Plus, Trash2, Users, Pencil, ShoppingBasket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Empty } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { SegmentDialog } from "@/components/SegmentDialog";
import { api, eur, num, errMsg } from "@/lib/api";

const describe = (r) => {
  const out = [];
  if (r.min_spent != null) out.push(`Spesa ≥ ${eur(r.min_spent)}`);
  if (r.min_points != null) out.push(`Punti ≥ ${num(r.min_points)}`);
  if (r.min_transactions != null) out.push(`Acquisti ≥ ${r.min_transactions}`);
  if (r.tiers?.length) out.push(`Livello: ${r.tiers.map((t) => t[0].toUpperCase() + t.slice(1)).join(", ")}`);
  if (r.status) out.push(`Stato: ${r.status}`);
  if (r.purchased_within_days) out.push(`Acquisto negli ultimi ${r.purchased_within_days} gg`);
  return out.length ? out : ["Tutti i clienti"];
};

export default function Segments() {
  const [segs, setSegs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [members, setMembers] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/segments").then((r) => setSegs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Eliminare il segmento "${s.name}"?`)) return;
    try {
      await api.delete(`/segments/${s.id}`);
      toast.success("Segmento eliminato");
    } catch (e) { toast.error(errMsg(e)); }
    load();
  };
  const showMembers = async (s) => {
    try { const r = await api.get(`/segments/${s.id}/members`); setMembers(r.data); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Segmenti di clienti" subtitle="Gruppi dinamici calcolati sui dati delle API">
        <Button data-testid="new-segment-button" onClick={() => setEditing({})} className="bg-blue-600 hover:bg-blue-700"><Plus className="mr-2 h-4 w-4" />Nuovo segmento</Button>
      </PageHeader>
      {segs && segs.length === 0 && <Empty text="Nessun segmento. Creane uno." />}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(segs || []).map((s, i) => (
          <div key={s.id} data-testid={`segment-card-${s.id}`} className="animate-rise group rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading font-semibold text-slate-900">{s.name}</h3>
                <p className="mt-0.5 text-sm text-slate-500">{s.kind && s.kind !== "rules" && <span data-testid={`segment-kind-${s.id}`} className="mr-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] text-violet-700">{s.kind === "static" ? "Lista fissa" : "Da prodotti · automatico"}</span>}{s.description}</p>
              </div>
              <div className="font-heading text-2xl font-semibold text-blue-600" data-testid={`segment-count-${s.id}`}>{num(s.count)}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {describe(s.rules).map((t) => <span key={t} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{t}</span>)}
            </div>
            <div className="mt-5 flex gap-2 border-t border-slate-100 pt-4">
              <Button size="sm" variant="outline" data-testid={`segment-members-${s.id}`} onClick={() => showMembers(s)}><Users className="mr-1.5 h-3.5 w-3.5" />Clienti</Button>
              <Button size="sm" variant="outline" data-testid={`segment-basket-${s.id}`} onClick={() => nav(`/paniere?segment=${s.id}`)}><ShoppingBasket className="mr-1.5 h-3.5 w-3.5" />Paniere</Button>
              <Button size="sm" variant="outline" data-testid={`segment-edit-${s.id}`} onClick={() => setEditing(s)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" data-testid={`segment-delete-${s.id}`} onClick={() => remove(s)} className="hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <SegmentDialog segment={editing} onClose={() => setEditing(null)} onSaved={load} />
      <Sheet open={!!members} onOpenChange={(o) => !o && setMembers(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-md" data-testid="segment-members-sheet">
          <SheetHeader><SheetTitle className="font-heading">{members?.segment.name} · {num(members?.total)} clienti</SheetTitle></SheetHeader>
          {members && members.total > members.members.length && <p className="mt-1 text-xs text-slate-400" data-testid="segment-members-note">Mostrati i primi {members.members.length} per spesa</p>}
          <div className="mt-4 divide-y divide-slate-100 pr-2">
            {members?.members.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3">
                <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-slate-400">{eur(c.total_spent)} · {num(c.points)} pt</div></div>
                <TierBadge tier={c.tier} />
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
